import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API } from '../App';
import { getImageUrl } from '../utils/imageUtils';
import { toast } from 'sonner';
import { 
  Building2, QrCode, Upload, ArrowRight, Check, Shield, 
  Copy, CheckCircle, Clock, AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

// Safe localStorage wrapper
const safeStorage = {
  getItem: (key) => { try { return localStorage.getItem(key); } catch { return null; } },
  setItem: (key, value) => { try { localStorage.setItem(key, value); } catch {} },
};

const Payment = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('bank_transfer');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotFile, setScreenshotFile] = useState(null);
  const [payNowQr, setPayNowQr] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [bankDetails, setBankDetails] = useState({
    accountName: "PHYSICSCO EXCELLENCE CENTER",
    bankName: "DBS Bank Ltd",
    accountNumber: "88537967575",
    bankCode: "7171",
    branchCode: "072",
    swiftCode: "DBSSSGSG",
    location: "Singapore"
  });

  const studentId = safeStorage.getItem('studentId');
  const studentEmail = safeStorage.getItem('studentEmail');
  const studentName = safeStorage.getItem('studentName');

  useEffect(() => {
    // Fetch courses
    axios.get(`${API}/public/courses`).then(res => {
      setCourses(res.data);
      if (res.data.length > 0) setSelectedCourse(res.data[0].id);
    }).catch(console.error);

    // Fetch PayNow QR code
    axios.get(`${API}/public/payment-settings/paynow-qr`).then(res => {
      if (res.data.qr_image_url) {
        setPayNowQr(res.data.qr_image_url);
      }
    }).catch(() => {});

    // Fetch bank details
    axios.get(`${API}/public/payment-settings/bank-details`).then(res => {
      setBankDetails({
        accountName: res.data.account_name || "PHYSICSCO EXCELLENCE CENTER",
        bankName: res.data.bank_name || "DBS Bank Ltd",
        accountNumber: res.data.account_number || "88537967575",
        bankCode: res.data.bank_code || "7171",
        branchCode: res.data.branch_code || "072",
        swiftCode: res.data.swift_code || "DBSSSGSG",
        location: res.data.location || "Singapore"
      });
    }).catch(() => {});
  }, []);

  const selectedCourseData = courses.find(c => c.id === selectedCourse);

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Only JPG, JPEG, PNG allowed.');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB.');
      return;
    }

    setScreenshotFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = () => setScreenshot(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!studentId) {
      toast.error('Please complete registration first');
      navigate('/register');
      return;
    }

    if (!selectedCourse) {
      toast.error('Please select a course');
      return;
    }

    if (!screenshotFile) {
      toast.error('Please upload payment screenshot');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('student_id', studentId);
      formData.append('student_name', studentName || '');
      formData.append('email', studentEmail || '');
      formData.append('course_id', selectedCourse);
      formData.append('course_name', selectedCourseData?.name || '');
      formData.append('amount', selectedCourseData?.price || 0);
      formData.append('payment_method', paymentMethod);
      formData.append('screenshot', screenshotFile);

      await axios.post(`${API}/payments/submit-proof`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setSubmitted(true);
      toast.success('Payment proof submitted successfully!');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to submit payment proof';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Success state
  if (submitted) {
    return (
      <div className="py-24 min-h-[70vh] flex items-center">
        <div className="container mx-auto px-4 md:px-6 max-w-xl">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="glass-card p-8 md:p-12 text-center"
          >
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-400" />
            </div>
            <h1 className="font-outfit text-3xl font-bold text-white mb-4">
              Payment Proof Submitted!
            </h1>
            <p className="text-slate-400 mb-4">
              Your payment proof has been submitted successfully. Our team will verify your payment and confirm your enrollment shortly.
            </p>
            <div className="bg-surface-navy/50 rounded-xl p-4 mb-6">
              <div className="flex items-center justify-center gap-2 text-electric-cyan">
                <Clock className="w-5 h-5" />
                <span className="font-medium">Verification typically takes 2–6 hours</span>
              </div>
            </div>
            <p className="text-slate-500 text-sm mb-8">
              You will receive an email confirmation once your payment is verified.
            </p>
            <Button onClick={() => navigate('/')} className="btn-primary">
              Return to Home
            </Button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6 max-w-3xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 bg-electric-cyan/10 border border-electric-cyan/30 rounded-full px-4 py-2 mb-6">
            <Shield className="w-4 h-4 text-electric-cyan" />
            <span className="text-electric-cyan text-sm font-medium">Secure Payment</span>
          </div>
          <h1 className="font-outfit text-4xl md:text-5xl font-bold text-white mb-4" data-testid="payment-title">
            Complete Payment
          </h1>
          <p className="text-slate-400">Pay via Bank Transfer or PayNow and upload your payment confirmation</p>
        </motion.div>

        {/* Course Selection */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-card p-6 mb-6"
        >
          <h2 className="font-outfit text-lg font-semibold text-white mb-4">Select Course</h2>
          <Select value={selectedCourse} onValueChange={setSelectedCourse}>
            <SelectTrigger className="input-dark" data-testid="payment-course-select">
              <SelectValue placeholder="Select a course" />
            </SelectTrigger>
            <SelectContent className="bg-deep-navy border-surface-navy">
              {courses.map(course => (
                <SelectItem key={course.id} value={course.id} className="text-white hover:bg-surface-navy">
                  {course.name} - SGD {course.price}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {selectedCourseData && (
            <div className="mt-4 p-4 bg-surface-navy/50 rounded-lg space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-slate-400">Selected Course</span>
                <span className="text-white font-medium">{selectedCourseData.name}</span>
              </div>
              <div className="flex justify-between items-center pt-2 border-t border-surface-navy">
                <span className="text-slate-400">Amount to Pay</span>
                <span className="text-2xl font-outfit font-bold text-electric-cyan">SGD {selectedCourseData.price}</span>
              </div>
            </div>
          )}
        </motion.div>

        {/* Payment Method Selection */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass-card p-6 mb-6"
        >
          <h2 className="font-outfit text-lg font-semibold text-white mb-4">Payment Method</h2>
          
          <div className="grid grid-cols-2 gap-4 mb-6">
            <button
              onClick={() => setPaymentMethod('bank_transfer')}
              className={`p-4 rounded-xl border transition-all flex flex-col items-center gap-2 ${
                paymentMethod === 'bank_transfer'
                  ? 'bg-electric-cyan/20 border-electric-cyan text-white shadow-lg shadow-electric-cyan/20'
                  : 'bg-surface-navy/50 border-surface-navy text-slate-400 hover:border-slate-500'
              }`}
              data-testid="payment-method-bank"
            >
              <Building2 className="w-8 h-8" />
              <span className="font-medium">Bank Transfer</span>
            </button>
            
            <button
              onClick={() => setPaymentMethod('paynow')}
              className={`p-4 rounded-xl border transition-all flex flex-col items-center gap-2 ${
                paymentMethod === 'paynow'
                  ? 'bg-electric-cyan/20 border-electric-cyan text-white shadow-lg shadow-electric-cyan/20'
                  : 'bg-surface-navy/50 border-surface-navy text-slate-400 hover:border-slate-500'
              }`}
              data-testid="payment-method-paynow"
            >
              <QrCode className="w-8 h-8" />
              <span className="font-medium">PayNow</span>
            </button>
          </div>

          {/* Bank Transfer Details */}
          {paymentMethod === 'bank_transfer' && (
            <div className="space-y-4">
              <div className="bg-surface-navy/50 rounded-xl p-5">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-electric-cyan" />
                  Bank Transfer Details
                </h3>
                <div className="space-y-3">
                  {[
                    { label: 'Account Name', value: bankDetails.accountName, key: 'accountName' },
                    { label: 'Bank Name', value: bankDetails.bankName, key: 'bankName' },
                    { label: 'Account Number', value: bankDetails.accountNumber, key: 'accountNumber' },
                    { label: 'Bank Code', value: bankDetails.bankCode, key: 'bankCode' },
                    { label: 'Branch Code', value: bankDetails.branchCode, key: 'branchCode' },
                    { label: 'SWIFT Code', value: bankDetails.swiftCode, key: 'swiftCode' },
                    { label: 'Location', value: bankDetails.location, key: 'location' },
                  ].map(({ label, value, key }) => (
                    <div key={key} className="flex justify-between items-center py-2 border-b border-surface-navy last:border-0">
                      <span className="text-slate-400 text-sm">{label}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium">{value}</span>
                        <button
                          onClick={() => copyToClipboard(value, key)}
                          className="p-1.5 rounded-lg hover:bg-surface-navy transition-colors"
                          title="Copy"
                        >
                          {copiedField === key ? (
                            <Check className="w-4 h-4 text-green-400" />
                          ) : (
                            <Copy className="w-4 h-4 text-slate-400" />
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
                <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                <p className="text-yellow-200 text-sm">
                  Please transfer the course fee using the above bank details. After completing the payment, upload the payment confirmation below.
                </p>
              </div>
            </div>
          )}

          {/* PayNow QR Code */}
          {paymentMethod === 'paynow' && (
            <div className="space-y-4">
              {payNowQr ? (
                <div className="flex flex-col items-center">
                  <div className="bg-white p-4 rounded-xl mb-4">
                    <img
                      src={getImageUrl(payNowQr)}
                      alt="PayNow QR Code"
                      className="w-56 h-56 object-contain"
                    />
                  </div>
                  <p className="text-center text-slate-400 text-sm mb-2">
                    Amount: <span className="text-electric-cyan font-bold text-lg">SGD {selectedCourseData?.price || 0}</span>
                  </p>
                </div>
              ) : (
                <div className="flex flex-col items-center p-8 bg-surface-navy/50 rounded-xl">
                  <QrCode className="w-16 h-16 text-slate-500 mb-4" />
                  <p className="text-slate-400">PayNow QR code not available</p>
                  <p className="text-slate-500 text-sm">Please use bank transfer instead</p>
                </div>
              )}
              
              <div className="flex items-start gap-3 p-4 bg-electric-cyan/10 border border-electric-cyan/30 rounded-xl">
                <QrCode className="w-5 h-5 text-electric-cyan flex-shrink-0 mt-0.5" />
                <p className="text-slate-300 text-sm">
                  Scan the QR code using your banking app to complete the payment. After payment, upload the confirmation screenshot below.
                </p>
              </div>
            </div>
          )}
        </motion.div>

        {/* Payment Screenshot Upload */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="glass-card p-6 mb-6"
        >
          <h2 className="font-outfit text-lg font-semibold text-white mb-4">
            Upload Payment Screenshot <span className="text-red-400">*</span>
          </h2>
          
          <div className="space-y-4">
            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png"
              onChange={handleFileChange}
              className="hidden"
              id="screenshot-upload"
              data-testid="payment-screenshot-input"
            />
            
            <label
              htmlFor="screenshot-upload"
              className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-surface-navy rounded-xl cursor-pointer hover:border-electric-cyan/50 transition-colors bg-surface-navy/20"
            >
              {screenshot ? (
                <div className="w-full max-w-xs">
                  <img
                    src={screenshot}
                    alt="Payment screenshot preview"
                    className="w-full rounded-lg mb-4"
                  />
                  <div className="flex items-center justify-center gap-2 text-green-400">
                    <Check className="w-5 h-5" />
                    <span>Screenshot uploaded</span>
                  </div>
                  <p className="text-slate-500 text-xs text-center mt-2">Click to change</p>
                </div>
              ) : (
                <>
                  <Upload className="w-12 h-12 text-slate-400 mb-4" />
                  <p className="text-white font-medium mb-2">Click to upload screenshot</p>
                  <p className="text-slate-500 text-sm">JPG, JPEG, PNG • Max 5MB</p>
                </>
              )}
            </label>

            {screenshotFile && (
              <p className="text-slate-400 text-sm text-center">
                Selected: {screenshotFile.name} ({(screenshotFile.size / 1024 / 1024).toFixed(2)} MB)
              </p>
            )}
          </div>
        </motion.div>

        {/* Submit Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Button
            onClick={handleSubmit}
            disabled={loading || !selectedCourse || !screenshotFile}
            className="btn-primary w-full h-14 text-lg"
            data-testid="payment-submit-btn"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-deep-blue"></div>
            ) : (
              <>
                Submit Payment Proof <ArrowRight className="w-5 h-5 ml-2" />
              </>
            )}
          </Button>
          
          <p className="text-center text-slate-500 text-sm mt-4">
            By submitting, you confirm that you have completed the payment.
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default Payment;
